import type { Toast } from 'types/common';
import { createWithEqualityFn } from 'zustand/traditional';

const getInitialBrowserPathname = () =>
  typeof globalThis.window === 'undefined'
    ? ''
    : globalThis.window.location.pathname;

export type ArtifactSurface = 'none' | 'panel' | 'main';

const closePanel = (dismiss: boolean, surface: ArtifactSurface) =>
  dismiss && surface === 'panel' ? 'none' : surface;

export interface ShellStore {
  artifactSurface: ArtifactSurface;
  browserPathname: string;
  isOffline: boolean;
  isQuestionPanelOpen: boolean;
  isRightPanelOpen: boolean;
  isSelectedAudienceTooltipOpen: boolean;
  isSidebarExpanded: boolean;
  isSourceInfoDialogOpen: boolean;
  pathname: string;
  rightPanelProps: {
    content?: string;
    title?: string;
    isOpen: boolean;
    onToggleCallback: (() => void) | null;
  };
  toast: Toast;
  setArtifactSurface: (surface: ArtifactSurface) => void;
  setIsOffline: (isOffline: boolean) => void;
  setIsQuestionPanelOpen: (open: boolean) => void;
  setIsSidebarExpanded: (expanded: boolean) => void;
  setIsRightPanelOpen: (open: boolean) => void;
  setIsSourceInfoDialogOpen: (open: boolean) => void;
  setIsSelectedAudienceTooltipOpen: (open: boolean) => void;
  setBrowserPathname: (pathname: string) => void;
  setPathname: (pathname: string) => void;
  setRightPanelProps: (props: ShellStore['rightPanelProps']) => void;
  setToast: (toast: Toast) => void;
  reset: () => void;
}

export const useShellStore = createWithEqualityFn<ShellStore>((set, get) => ({
  artifactSurface: 'none',
  browserPathname: getInitialBrowserPathname(),
  isOffline: false,
  isQuestionPanelOpen: false,
  isRightPanelOpen: false,
  isSidebarExpanded: true,
  isSelectedAudienceTooltipOpen: true,
  isSourceInfoDialogOpen: false,
  pathname: '',
  rightPanelProps: {
    isOpen: false,
    onToggleCallback: null
  },
  toast: { isOpen: false, message: '' },
  // The panel shares its slot with the right panel, so it takes it over. The
  // main view replaces 'Main' altogether, which resets the shell on unmount.
  setArtifactSurface: (surface) =>
    set({
      artifactSurface: surface,
      isRightPanelOpen: surface === 'panel' ? false : get().isRightPanelOpen,
      rightPanelProps:
        surface === 'panel'
          ? { ...get().rightPanelProps, isOpen: false }
          : get().rightPanelProps
    }),
  setBrowserPathname: (browserPathname) => set({ browserPathname }),
  setPathname: (pathname) => set({ pathname }),
  setIsOffline: (isOffline) => set({ isOffline }),
  setIsQuestionPanelOpen: (open) => set({ isQuestionPanelOpen: open }),
  setIsSidebarExpanded: (expanded) => set({ isSidebarExpanded: expanded }),
  setIsRightPanelOpen: (open) => {
    set({
      isRightPanelOpen: open,
      artifactSurface: closePanel(open, get().artifactSurface),
      rightPanelProps: { ...get().rightPanelProps, isOpen: open }
    });
    get().rightPanelProps.onToggleCallback?.();
  },
  setToast: (toast) =>
    set({
      toast: {
        isOpen: Boolean(toast.message),
        ...toast,
        position: toast.position || 'absolute',
        timeout: toast.timeout || 2000
      }
    }),
  setIsSourceInfoDialogOpen: (open) => set({ isSourceInfoDialogOpen: open }),
  setIsSelectedAudienceTooltipOpen: (open) =>
    set({ isSelectedAudienceTooltipOpen: open }),
  setRightPanelProps: (props) =>
    set({
      rightPanelProps: props,
      isRightPanelOpen: props.isOpen,
      artifactSurface: closePanel(props.isOpen, get().artifactSurface)
    }),
  // The main view is driven by the URL, so it is deliberately left untouched
  // here: 'Main' resets the store while unmounting, which happens as soon as
  // the main view replaces it.
  reset: () =>
    set((state) => ({
      artifactSurface: closePanel(true, state.artifactSurface),
      isQuestionPanelOpen: false,
      isRightPanelOpen: false,
      rightPanelProps: {
        isOpen: false,
        onToggleCallback: null
      }
    }))
}));
